import { useId } from "react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

/**
 * Form primitives for the site's own forms — the ones that replaced the
 * HubSpot embeds. Styling comes from theme tokens only (see CLAUDE.md), so
 * these inherit the palette rather than carrying brand colours of their own.
 */

const fieldClasses =
  "w-full rounded-xl border border-rule bg-background px-4 py-2.5 text-sm text-foreground " +
  "tracking-mono placeholder:text-muted-foreground/60 transition-colors duration-200 " +
  "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 " +
  "disabled:cursor-not-allowed disabled:opacity-60";

interface LabelledFieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  children: (props: { id: string; describedBy?: string }) => ReactNode;
}

function LabelledField({ label, required, hint, children }: LabelledFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground"
      >
        {label}
        {required && <span className="text-primary-500"> *</span>}
      </label>
      {children({ id, describedBy: hintId })}
      {hint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  );
}

type TextFieldProps = {
  label: string;
  hint?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export function TextField({ label, hint, required, ...props }: TextFieldProps) {
  return (
    <LabelledField label={label} required={required} hint={hint}>
      {({ id, describedBy }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          required={required}
          className={fieldClasses}
          {...props}
        />
      )}
    </LabelledField>
  );
}

type TextAreaFieldProps = {
  label: string;
  hint?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextAreaField({ label, hint, required, rows = 4, ...props }: TextAreaFieldProps) {
  return (
    <LabelledField label={label} required={required} hint={hint}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          rows={rows}
          aria-describedby={describedBy}
          required={required}
          className={`${fieldClasses} resize-y`}
          {...props}
        />
      )}
    </LabelledField>
  );
}

interface CheckboxFieldProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function CheckboxField({ label, checked, onChange, disabled }: CheckboxFieldProps) {
  const id = useId();

  return (
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-rule accent-[var(--primary-500)] disabled:cursor-not-allowed"
      />
      <label htmlFor={id} className="cursor-pointer text-sm leading-relaxed text-muted-foreground">
        {label}
      </label>
    </div>
  );
}

/**
 * A field real visitors never see and never focus. Bots that fill every input
 * trip it, and the server then pretends the submission succeeded. Hidden with
 * inline styles rather than `hidden` so it stays in the DOM and in the form
 * payload; `tabIndex={-1}` and `aria-hidden` keep it away from keyboard and
 * screen-reader users.
 */
export function HoneypotField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div
      aria-hidden="true"
      style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}
    >
      <label htmlFor="company-website-hp">Company website</label>
      <input
        id="company-website-hp"
        name="company_website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** Inline error, announced to assistive tech when it appears. */
export function FormError({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
    >
      {message}
    </p>
  );
}
